import {
  useCallback,
  useEffect,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { TextField } from "@/components/TextField";
import { SelectField } from "@/components/SelectField";
import { AccountForm } from "@/components/AccountForm";
import { SubmissionFlowProgress } from "@/components/SubmissionFlowProgress";
import {
  listAccounts,
  createDraftAccount,
  updateAccount,
  listIndustries,
  type AccountSummary,
  type AccountDetail,
  type AccountRecord,
  type NamedRef,
} from "@/lib/brokerAccounts";
import {
  createSubmission,
  findExistingDraft,
  listAccountSubmissions,
  type AccountSubmission,
  type CodeDisplayRef,
  type CreateSubmissionInput,
  type Submission,
} from "@/lib/brokerSubmissions";
import {
  getProducts,
  productOptions,
  accountFloorFields,
  type BrokerProduct,
} from "@/lib/brokerProducts";
import { DocumentDropzone } from "@/components/DocumentDropzone";
import {
  ExtractedMarker,
  ExtractedReviewBanner,
} from "@/components/ExtractedReview";
import type { IntakeProposal } from "@/lib/brokerDocuments";
import { selectUploader } from "@/lib/uploadSelection";
import {
  proposalToStep1,
  proposalToAccountSeed,
  proposalToSubmissionBag,
  type UnmatchedCode,
} from "@/lib/documentPrefill";
import { US_STATE_OPTIONS } from "@/lib/usStates";
import { messageFor } from "@/lib/errors";

// BRK-S038 — the router-navigation state that carries the extracted submission
// field bag from step 2 to the submission detail WITHOUT persisting it (no
// auto-commit): the detail's manifest details form consumes it as prefill the
// broker still confirms. Session-scoped UI state (NN #9); lost on a reload, which
// is fine — nothing was committed, so the broker just sees the manual form.
export interface DocumentPrefillNavState {
  documentPrefill?: {
    bag: Record<string, unknown>;
    extractedKeys: string[];
    unmatchedCodes: UnmatchedCode[];
    codeRefs: Record<string, CodeDisplayRef>;
  };
}

// Which uploader this dropzone gets — the offline simulator for an active
// dev-login session, the real direct-to-storage XHR for everything else. The
// rule moved to `@/lib/uploadSelection` in BRK-S120, when the bond flow's
// dropzone became its second real caller; it has been wrong once (in a way only
// a real session could show), so it is worth exactly one definition.

// Broker submission intake (BRK-S021, live on PMS-S125). Choose New Business or
// Renewal; a submission starts as a minimal DRAFT on an own account.
// - New Business (BRK-S041): a net-new insured — capture legal name + state +
//   coverage, create a DRAFT account (PMS-S134 POST /accounts/draft) then start
//   the submission on it.
// - Renewal: pick products to renew off the account's prior submissions.
// The document dropzone renders but the manual controls are what's wired here —
// upload + "Confirm document details" is the document-AI follow-up (BRK-S038).
type Mode = "new_business" | "renewal";

// Single-select sentinel for the "new product — not previously carried" row in
// the renewal picker (BRK-S048).
const NEW_PRODUCT = "__new_product__";

export function NewSubmissionPage() {
  const { authedRequest } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState<Mode>("new_business");
  // After a New Business draft is created, the flow pauses on the profile step
  // (BRK-S043) to complete the insured account before landing on the detail. On
  // the document path (BRK-S038) it carries the extracted account seed + the
  // marked keys so step 2 prefills the SAME AccountForm and marks the fields.
  const [nbProfile, setNbProfile] = useState<{
    account: AccountDetail;
    submission: Submission;
    state: string;
    accountSeed?: AccountRecord;
    accountExtractedKeys?: string[];
    submissionPrefill?: DocumentPrefillNavState["documentPrefill"];
  } | null>(null);
  // The document-intake proposal (BRK-S038), held from analysis through steps
  // 1–2 so it can prefill each. Null on the manual path.
  const [proposal, setProposal] = useState<IntakeProposal | null>(null);
  // Step-1 fields still holding an unconfirmed extracted value (mark → clear on
  // edit). A plain Set so it can be re-seeded when a proposal arrives after
  // mount (the useExtractedReview hook seeds once, which doesn't fit here).
  const [step1Pending, setStep1Pending] = useState<Set<string>>(new Set());
  const touchStep1 = useCallback((key: string) => {
    setStep1Pending((current) => {
      if (!current.has(key)) return current;
      const next = new Set(current);
      next.delete(key);
      return next;
    });
  }, []);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  // New Business fields. Industry is required at step 1 (BRK-S051) and sent on
  // the draft-account create, so the insured's industry is known from the first
  // touch; options come from the existing reference endpoint.
  const [legalName, setLegalName] = useState("");
  const [state, setState] = useState("");
  const [industryId, setIndustryId] = useState("");
  const [coverage, setCoverage] = useState("");
  const [industries, setIndustries] = useState<NamedRef[] | null>(null);
  const [industriesError, setIndustriesError] = useState<string | null>(null);

  // The product picker is server-driven from the PMS catalog (BRK-S051) —
  // options are product-id keyed (the create is product-addressed, PMS-S147),
  // labeled with the product names. Shared by New Business and the renewal
  // new-product dropdown.
  const [coverageOptions, setCoverageOptions] = useState<
    { value: string; label: string }[] | null
  >(null);
  const [products, setProducts] = useState<BrokerProduct[]>([]);
  const [productsError, setProductsError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Independent loads: either failing is handled inline (NN #7) while the
      // rest of the form stays usable; the broker can reload to retry.
      try {
        const page = await listIndustries(authedRequest);
        if (!cancelled) setIndustries(page.items);
      } catch {
        if (!cancelled)
          setIndustriesError("Couldn’t load industries — reload to try again.");
      }
      try {
        const { items } = await getProducts(authedRequest);
        if (!cancelled) {
          setProducts(items);
          setCoverageOptions(productOptions(items));
        }
      } catch {
        if (!cancelled)
          setProductsError("Couldn’t load products — reload to try again.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest]);

  // Renewal selection. A renewal is exactly ONE submission (BRK-S048), so the
  // prior-submissions table is single-select: `selectedId` is a prior
  // submission's id, or the NEW_PRODUCT sentinel for a not-previously-carried
  // coverage (which then reads `newCoverage`).
  const [account, setAccount] = useState<AccountSummary | null>(null);
  const [priorSubs, setPriorSubs] = useState<AccountSubmission[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newCoverage, setNewCoverage] = useState("");
  // A draft already in flight for the chosen account + product (BRK-S063): hold
  // the intended create so the broker can resume the existing one or override.
  const [duplicate, setDuplicate] = useState<{
    existing: AccountSubmission;
    input: CreateSubmissionInput;
  } | null>(null);

  const loadPrior = useCallback(
    async (accountId: string) => {
      setPriorSubs(null);
      try {
        const { items } = await listAccountSubmissions(
          authedRequest,
          accountId,
        );
        setPriorSubs(items);
      } catch (err) {
        setError(messageFor(err));
        setPriorSubs([]);
      }
    },
    [authedRequest],
  );

  useEffect(() => {
    if (account) void loadPrior(account.id);
  }, [account, loadPrior]);

  // Create ONE submission and land straight on its detail (BRK-S045 + S048) —
  // no "Submission created" interstitial, no multi-create loop.
  async function createAndGo(input: CreateSubmissionInput) {
    setError(null);
    setPending(true);
    try {
      const submission = await createSubmission(authedRequest, input);
      navigate(`/submissions/${submission.id}`);
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setPending(false);
    }
  }

  // Document intake analyzed (BRK-S038): prefill step 1 (legal name + state) and
  // mark them for review. Industry + product stay the broker's choice —
  // extraction proposes the insured, not the coverage. Nothing auto-advances.
  const handleProposal = useCallback((p: IntakeProposal) => {
    setProposal(p);
    const s1 = proposalToStep1(p);
    if (s1.legalName) setLegalName(s1.legalName);
    if (s1.state) setState(s1.state);
    setStep1Pending(new Set(s1.extractedKeys));
    setError(null);
  }, []);

  // New Business: create a minimal DRAFT account (PMS-S134), then start the
  // submission on it. Two calls, one flow; either failing surfaces inline.
  async function onSubmitNewBusiness(e: FormEvent) {
    e.preventDefault();
    if (!legalName.trim()) {
      setError("Enter the insured's legal name.");
      return;
    }
    if (!state.trim()) {
      setError("Enter the state.");
      return;
    }
    if (!industryId) {
      setError("Select the insured’s industry.");
      return;
    }
    if (!coverage) {
      setError("Choose the coverage you’re seeking.");
      return;
    }
    setError(null);
    setPending(true);
    try {
      const st = state.trim().toUpperCase();
      const account = await createDraftAccount(authedRequest, {
        legalName: legalName.trim(),
        state: st,
        industryId,
      });
      const submission = await createSubmission(authedRequest, {
        accountId: account.id,
        productId: coverage,
      });
      // On the document path, build the step-2 account seed + the step-3
      // submission-bag prefill from the proposal (single capture surface — the
      // SAME AccountForm + details form the manual path uses).
      let accountSeed: AccountRecord | undefined;
      let accountExtractedKeys: string[] | undefined;
      let submissionPrefill:
        | DocumentPrefillNavState["documentPrefill"]
        | undefined;
      if (proposal) {
        // Seed the address state from step 1 so the form shows it even when the
        // document had no address; extraction overlays the rest.
        const stateSeed: AccountRecord = {
          ...account,
          legalAddress: {
            id: "",
            street: "",
            city: "",
            state: st,
            zip: "",
            formattedAddress: null,
          },
        };
        const seeded = proposalToAccountSeed(proposal, stateSeed);
        accountSeed = seeded.seed;
        accountExtractedKeys = seeded.extractedKeys;
        const manifest =
          products.find((p) => p.id === coverage)?.manifest ?? null;
        if (manifest) {
          const sub = proposalToSubmissionBag(proposal, manifest);
          if (sub.extractedKeys.length || sub.unmatchedCodes.length) {
            submissionPrefill = {
              bag: sub.bag,
              extractedKeys: sub.extractedKeys,
              unmatchedCodes: sub.unmatchedCodes,
              codeRefs: sub.codeRefs,
            };
          }
        }
      }
      // Pause on the profile step to complete the insured account (BRK-S043);
      // the submission is already started, so this is skippable.
      setNbProfile({
        account,
        submission,
        state: st,
        accountSeed,
        accountExtractedKeys,
        submissionPrefill,
      });
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setPending(false);
    }
  }

  async function onProceedRenewal() {
    if (!account) return;
    if (!selectedId) {
      setError("Select a product to continue.");
      return;
    }
    // Resolve the create body + the product being started, so we can check for a
    // duplicate draft first (BRK-S063). For a renewal-of-prior the body names
    // only the prior submission (PMS copies its product + field bag forward,
    // PMS-S147/S148); the product id for the check is resolved from the prior's
    // coverage line via the catalog.
    let input: CreateSubmissionInput;
    let productId: string | undefined;
    if (selectedId === NEW_PRODUCT) {
      if (!newCoverage) {
        setError("Choose the new coverage.");
        return;
      }
      input = { accountId: account.id, productId: newCoverage };
      productId = newCoverage;
    } else {
      const prior = (priorSubs ?? []).find((s) => s.id === selectedId);
      if (!prior) {
        setError("Select a product to continue.");
        return;
      }
      input = { accountId: account.id, renewalOfSubmissionId: prior.id };
      productId = products.find(
        (p) => p.lineOfBusiness === prior.coverageKey,
      )?.id;
    }

    // Cheap duplicate-draft check before creating (PMS-S151 filters). Skipped
    // when the product id can't be resolved (a line carrying several products —
    // flagged to PMS as a follow-up on the enriched list item); a failed check
    // never blocks the create (NN #7).
    if (productId) {
      setError(null);
      setPending(true);
      try {
        const existing = await findExistingDraft(
          authedRequest,
          account.id,
          productId,
        );
        if (existing) {
          setDuplicate({ existing, input });
          return;
        }
      } catch {
        // Existence check failed — fall through to a normal create.
      } finally {
        setPending(false);
      }
    }
    void createAndGo(input);
  }

  // New Business step 2 (conditional): complete the insured profile, or finish
  // later. Completing continues to the submission detail (rating); "Finish later"
  // routes to /submissions (Option B — never onto an unratable rating screen).
  // The account-floor gate on the detail protects the deferred path (BRK-S046).
  if (nbProfile) {
    // The step-2 floor is the chosen product's account-scope INDICATION floor
    // (BRK-S057) — address only for WC; email/phone/personnel become optional.
    const manifest = products.find((p) => p.id === coverage)?.manifest ?? null;
    const floorFieldKeys = manifest
      ? accountFloorFields(manifest, "indication").map((f) => f.key)
      : undefined;
    // Carry the extracted submission bag to the detail as router state (never
    // persisted — no auto-commit, NN #9). The details form there marks it for
    // review; a reload just drops back to the manual form (nothing committed).
    const detailNavOptions = nbProfile.submissionPrefill
      ? {
          state: {
            documentPrefill: nbProfile.submissionPrefill,
          } satisfies DocumentPrefillNavState,
        }
      : undefined;
    return (
      <NewBusinessProfileStep
        account={nbProfile.account}
        submission={nbProfile.submission}
        state={nbProfile.state}
        floorFieldKeys={floorFieldKeys}
        extractedSeed={nbProfile.accountSeed}
        extractedKeys={nbProfile.accountExtractedKeys}
        onComplete={() =>
          navigate(`/submissions/${nbProfile.submission.id}`, detailNavOptions)
        }
        onFinishLater={() => navigate("/submissions")}
      />
    );
  }

  return (
    <section className="mx-auto flex w-full max-w-3xl flex-col gap-6 py-8">
      <SubmissionFlowProgress current={1} />
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold tracking-tight">New submission</h1>
        <p className="text-brk-dim">
          Drop a document and we’ll pull in the details, or start from scratch.
        </p>
      </div>

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-sm font-medium text-brk-text">
          New business or renewal?
        </legend>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <ModeCard
            selected={mode === "new_business"}
            onSelect={() => setMode("new_business")}
            title="New business"
            subtitle="For new accounts."
          />
          <ModeCard
            selected={mode === "renewal"}
            onSelect={() => setMode("renewal")}
            title="Renewal / existing client"
            subtitle="New coverage or renewing an existing account."
            icon={
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 5h11l4 4v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z" />
                <path d="M8.5 13a3 3 0 0 1 5.6-1.2M8 10.5V13h2.5M15.5 15a3 3 0 0 1-5.6 1.2M16 16.5V14h-2.5" />
              </svg>
            }
          />
        </div>
      </fieldset>

      {error && (
        <p role="alert" className="text-sm text-brk-danger">
          {error}
        </p>
      )}

      {duplicate && (
        <div
          role="alertdialog"
          aria-label="Existing draft found"
          className="flex flex-col gap-3 rounded-md border border-brk-accent/40 bg-brk-bg px-4 py-3 text-sm text-brk-text"
        >
          <p>
            You already have a draft submission{" "}
            <span className="font-mono font-medium">
              {duplicate.existing.submissionNumber}
            </span>{" "}
            for {duplicate.existing.coverage}
            {account ? ` on ${account.legalName}` : ""}. Resume that draft
            instead of starting a duplicate?
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(`/submissions/${duplicate.existing.id}`)}
              className="rounded-md bg-brk-accent px-4 py-2 font-medium text-white transition-colors hover:bg-brk-accent-strong"
            >
              Resume draft
            </button>
            <button
              type="button"
              onClick={() => {
                const { input } = duplicate;
                setDuplicate(null);
                void createAndGo(input);
              }}
              className="text-sm text-brk-accent hover:underline"
            >
              Start a new one
            </button>
          </div>
        </div>
      )}

      {mode === "new_business" ? (
        <form className="flex flex-col gap-4" onSubmit={onSubmitNewBusiness}>
          <DocumentDropzone
            authedRequest={authedRequest}
            onProposal={handleProposal}
            uploader={selectUploader()}
          />
          <div className="flex items-center gap-3 text-xs uppercase tracking-wide text-brk-dim">
            <span className="h-px flex-1 bg-brk-border" />
            {proposal ? "Review the details" : "Or enter the details yourself"}
            <span className="h-px flex-1 bg-brk-border" />
          </div>
          <ExtractedReviewBanner count={step1Pending.size} />
          <div className="flex flex-col gap-1">
            <TextField
              label="Insured legal name"
              placeholder="e.g. Ironbridge Manufacturing, Inc."
              value={legalName}
              onChange={(e) => {
                setLegalName(e.target.value);
                touchStep1("legalName");
              }}
            />
            {step1Pending.has("legalName") && <ExtractedMarker />}
          </div>
          <div className="flex flex-col gap-1">
            <SelectField
              label="State"
              placeholder="Select a state"
              options={US_STATE_OPTIONS}
              value={state}
              onChange={(e) => {
                setState(e.target.value);
                touchStep1("state");
              }}
            />
            {step1Pending.has("state") && <ExtractedMarker />}
          </div>
          <SelectField
            label="Industry"
            placeholder={
              industries === null ? "Loading industries…" : "Select an industry"
            }
            options={(industries ?? []).map((i) => ({
              value: i.id,
              label: i.name,
            }))}
            value={industryId}
            onChange={(e) => setIndustryId(e.target.value)}
            disabled={industries === null && !industriesError}
            error={industriesError ?? undefined}
          />
          <SelectField
            label="Coverage type you are seeking"
            placeholder={
              coverageOptions === null ? "Loading products…" : "Select coverage"
            }
            options={coverageOptions ?? []}
            value={coverage}
            onChange={(e) => setCoverage(e.target.value)}
            disabled={coverageOptions === null && !productsError}
            error={productsError ?? undefined}
          />
          <button
            type="submit"
            disabled={pending}
            className="self-start rounded-md bg-brk-accent px-4 py-2 font-medium text-white transition-colors hover:bg-brk-accent-strong disabled:opacity-60"
          >
            {pending
              ? "Creating…"
              : proposal
                ? "Continue"
                : "Start without a document"}
          </button>
        </form>
      ) : (
        <RenewalPanel
          account={account}
          onSelectAccount={setAccount}
          onChangeAccount={() => {
            setAccount(null);
            setSelectedId(null);
          }}
          priorSubs={priorSubs}
          selectedId={selectedId}
          onSelect={setSelectedId}
          newCoverage={newCoverage}
          onNewCoverage={setNewCoverage}
          coverageOptions={coverageOptions ?? []}
          pending={pending}
          onProceed={onProceedRenewal}
        />
      )}

      <Link to="/portal" className="text-sm text-brk-accent hover:underline">
        ← Back to the API Exchange
      </Link>
    </section>
  );
}

function ModeCard({
  selected,
  onSelect,
  title,
  subtitle,
  icon,
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  subtitle: string;
  icon?: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={`flex items-center gap-4 rounded-xl border p-5 text-left transition-colors ${
        selected
          ? "border-brk-accent bg-brk-accent/5 ring-1 ring-brk-accent"
          : "border-brk-border bg-brk-panel hover:border-brk-accent/60"
      }`}
    >
      <span
        aria-hidden="true"
        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${
          selected
            ? "bg-brk-accent/10 text-brk-accent"
            : "bg-brk-bg text-brk-dim"
        }`}
      >
        {icon ?? (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Zm7 0v5h5" />
            <path d="M11 13h4M13 11v4" />
          </svg>
        )}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="font-semibold text-brk-text">{title}</span>
        <span className="text-sm text-brk-dim">{subtitle}</span>
      </span>
      <span aria-hidden="true" className="shrink-0 text-lg text-brk-dim">
        ›
      </span>
    </button>
  );
}

// New Business step 2 (BRK-S043): complete the insured profile that finishes the
// draft account. Reuses the shared AccountForm — the single capture surface for
// both the manual path (seeded from the draft here) and, later, document
// autopopulation (BRK-S038 seeds the same form from extracted values). Saving
// completes the draft to `active` via the existing broker PUT (PMS-S134);
// "Finish later" leaves it a draft. Either way the parent shows the confirmation.
function NewBusinessProfileStep({
  account,
  submission,
  state,
  floorFieldKeys,
  extractedSeed,
  extractedKeys,
  onComplete,
  onFinishLater,
}: {
  account: AccountDetail;
  submission: Submission;
  state: string;
  /** The chosen product's indication account floor (BRK-S057). undefined =
   *  manifest not resolved → the form falls back to its legacy full floor. */
  floorFieldKeys?: string[];
  /** On the document path (BRK-S038), the AccountForm seed built from the
   *  proposal's account fields (single capture surface); the marked keys go with
   *  it. Omitted on the manual path — the form seeds from the draft + step-1
   *  state. */
  extractedSeed?: AccountRecord;
  extractedKeys?: string[];
  onComplete: () => void;
  onFinishLater: () => void;
}) {
  const { identity, authedRequest } = useAuth();
  // Manual path: seed from the draft (legal name) + the state captured in step 1,
  // onto the legal address so AccountForm shows a single address block (mailing
  // mirrors legal by default) with the state prefilled. Document path: use the
  // extraction-built seed (already carries the step-1 state) — the SAME form.
  const seed: AccountRecord = extractedSeed ?? {
    ...account,
    legalAddress: {
      id: "",
      street: "",
      city: "",
      state,
      zip: "",
      formattedAddress: null,
    },
  };

  return (
    <section className="mx-auto flex w-full max-w-2xl flex-col gap-6 py-8">
      <SubmissionFlowProgress current={2} />
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold tracking-tight">
          Complete the insured profile
        </h1>
        <p className="text-brk-dim">
          <span className="font-mono font-medium text-brk-text">
            {submission.submissionNumber}
          </span>{" "}
          started. Add the insured’s address to get an indication — or finish
          later (more detail is only needed to bind).
        </p>
      </div>
      <AccountForm
        initial={seed}
        withServicingBroker
        floorFieldKeys={floorFieldKeys}
        confirmStateAgainst={state}
        extractedKeys={extractedKeys}
        initialServicingBrokerId={identity?.brokerId ?? null}
        submitLabel="Save & continue"
        pendingLabel="Saving…"
        onSubmit={async (input) => {
          await updateAccount(authedRequest, account.id, input);
          onComplete();
        }}
        cancelSlot={
          <button
            type="button"
            onClick={onFinishLater}
            className="text-sm text-brk-accent hover:underline"
          >
            Finish later
          </button>
        }
      />
    </section>
  );
}

function RenewalPanel(props: {
  account: AccountSummary | null;
  onSelectAccount: (a: AccountSummary) => void;
  onChangeAccount: () => void;
  priorSubs: AccountSubmission[] | null;
  selectedId: string | null;
  onSelect: (id: string) => void;
  newCoverage: string;
  onNewCoverage: (v: string) => void;
  coverageOptions: { value: string; label: string }[];
  pending: boolean;
  onProceed: () => void;
}) {
  const { account } = props;
  return (
    <div className="flex flex-col gap-4">
      {!account ? (
        <AccountSearch onSelect={props.onSelectAccount} />
      ) : (
        <>
          <div className="flex items-center justify-between gap-3 rounded-lg border border-brk-border bg-brk-panel p-4">
            <div className="flex min-w-0 flex-col">
              <span className="truncate font-medium text-brk-text">
                {account.legalName}
              </span>
              <span className="truncate text-sm text-brk-dim">
                {account.accountNumber}
              </span>
            </div>
            <button
              type="button"
              onClick={props.onChangeAccount}
              className="shrink-0 text-sm text-brk-accent hover:underline"
            >
              Change
            </button>
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium text-brk-text">
              Select a product to renew
            </p>
            {props.priorSubs === null ? (
              <p role="status" className="text-sm text-brk-dim">
                Loading products…
              </p>
            ) : (
              <PriorSubmissionsTable
                rows={props.priorSubs}
                selectedId={props.selectedId}
                onSelect={props.onSelect}
                newCoverage={props.newCoverage}
                onNewCoverage={props.onNewCoverage}
                coverageOptions={props.coverageOptions}
              />
            )}
          </div>

          <button
            type="button"
            onClick={props.onProceed}
            disabled={props.pending}
            className="self-end rounded-md bg-brk-accent px-4 py-2 font-medium text-white transition-colors hover:bg-brk-accent-strong disabled:opacity-60"
          >
            {props.pending ? "Creating…" : "Proceed"}
          </button>
        </>
      )}
    </div>
  );
}

function AccountSearch({
  onSelect,
}: {
  onSelect: (a: AccountSummary) => void;
}) {
  const { authedRequest } = useAuth();
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<AccountSummary[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSearch(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const page = await listAccounts(authedRequest, {
        search: search.trim() || undefined,
        limit: 10,
        offset: 0,
      });
      setResults(page.items);
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <form className="flex gap-2" onSubmit={onSearch}>
        <input
          type="search"
          // Distinct from the shell's "Search all accounts" (BRK-S074) — the
          // header search is on this page too, and one name for both leaves a
          // screen reader with no way to tell them apart.
          aria-label="Search accounts for this submission"
          placeholder="Search accounts by name, city, or state"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full flex-1 rounded-md border border-brk-border bg-brk-panel px-3 py-2 text-brk-text outline-none focus:border-brk-accent focus:ring-2 focus:ring-brk-accent/30"
        />
        <button
          type="submit"
          className="rounded-md border border-brk-border px-4 py-2 font-medium text-brk-accent transition-colors hover:bg-brk-bg"
        >
          Search
        </button>
      </form>
      {error && (
        <p role="alert" className="text-sm text-brk-danger">
          {error}
        </p>
      )}
      {loading && (
        <p role="status" className="text-sm text-brk-dim">
          Searching…
        </p>
      )}
      {results && results.length === 0 && (
        <p className="text-sm text-brk-dim">No accounts found.</p>
      )}
      {results && results.length > 0 && (
        <ul className="flex flex-col gap-2">
          {results.map((a) => (
            <li key={a.id}>
              <button
                type="button"
                onClick={() => onSelect(a)}
                className="flex w-full items-center justify-between gap-3 rounded-lg border border-brk-border bg-brk-panel p-4 text-left transition-colors hover:border-brk-accent"
              >
                <span className="flex min-w-0 flex-col">
                  <span className="truncate font-medium text-brk-text">
                    {a.legalName}
                  </span>
                  <span className="truncate text-sm text-brk-dim">
                    {a.accountNumber}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PriorSubmissionsTable({
  rows,
  selectedId,
  onSelect,
  newCoverage,
  onNewCoverage,
  coverageOptions,
}: {
  rows: AccountSubmission[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  newCoverage: string;
  onNewCoverage: (v: string) => void;
  coverageOptions: { value: string; label: string }[];
}) {
  // A renewal is exactly one submission (BRK-S048), so the picker is a single
  // radio group. The "new product" row is one more radio option — whether a
  // not-previously-carried coverage belongs in renewal at all is the open
  // decision flagged in the PR.
  return (
    <div className="overflow-x-auto rounded-lg border border-brk-border">
      <table className="w-full min-w-[36rem] text-left text-sm">
        <thead className="bg-brk-bg text-xs uppercase tracking-wide text-brk-dim">
          <tr>
            <th className="w-8 px-3 py-2" />
            <th className="px-3 py-2">Product</th>
            <th className="px-3 py-2">Limit</th>
            <th className="px-3 py-2">Expiration</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">Carrier</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            // Only a completed prior can be renewed — PMS rejects a renewal of a
            // draft with a structured 400 (PMS-S152). Disable the row here so the
            // broker can't pick one, with a short explanation of why (BRK-S064).
            const isDraft = r.status === "draft";
            return (
              <tr
                key={r.id}
                className={`border-t border-brk-border ${isDraft ? "opacity-60" : ""}`}
              >
                <td className="px-3 py-2">
                  <input
                    type="radio"
                    name="renewal-product"
                    aria-label={`Renew ${r.coverage}`}
                    checked={selectedId === r.id}
                    disabled={isDraft}
                    onChange={() => onSelect(r.id)}
                  />
                </td>
                <td className="px-3 py-2 font-medium text-brk-text">
                  {r.coverage}
                  {isDraft && (
                    <span className="mt-0.5 block text-xs font-normal text-brk-dim">
                      Draft — finish it before renewing.
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-brk-dim">
                  {r.projectedLimit ?? "—"}
                </td>
                <td className="px-3 py-2 text-brk-dim">
                  {r.expirationDate ?? "—"}
                </td>
                <td className="px-3 py-2 text-brk-dim">
                  {statusLabel(r.status)}
                </td>
                <td className="px-3 py-2 text-brk-dim">{r.carrier ?? "—"}</td>
              </tr>
            );
          })}
          <tr className="border-t border-brk-border bg-brk-bg/40">
            <td className="px-3 py-2">
              <input
                type="radio"
                name="renewal-product"
                aria-label="Add a new product or coverage"
                checked={selectedId === NEW_PRODUCT}
                onChange={() => onSelect(NEW_PRODUCT)}
              />
            </td>
            <td className="px-3 py-2" colSpan={5}>
              <span className="font-medium text-brk-text">New product</span>{" "}
              <span className="text-brk-dim">— not listed above</span>
              {selectedId === NEW_PRODUCT && (
                <div className="mt-2 max-w-xs">
                  <SelectField
                    label="New coverage"
                    placeholder="Select coverage"
                    options={coverageOptions}
                    value={newCoverage}
                    onChange={(e) => onNewCoverage(e.target.value)}
                  />
                </div>
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function statusLabel(status: string): string {
  return status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
